// ─── Config ────────────────────────────────────────────────────────────────

const CHECKBOX_GROUPS = {
  'model[]':            'model',
  'location[]':         'location',
  'category[]':         'category',
  'warranty_period[]':  'warrantyPeriod',
  'fuel_type[]':        'fuelType',
}

const parseNum = v => {
  const n = +String(v).replace(/[\s ]/g, '')
  return isNaN(n) ? 0 : n
}

const RANGES = [
  {
    minName: 'price_min',
    maxName: 'price_max',
    format: v => {
      const n = parseNum(v)
      if (n < 1000) return n.toLocaleString('fr-FR') + ' €'
      return Math.round(n / 1000).toLocaleString('fr-FR') + ' k€'
    },
  },
  { minName: 'loa_min',   maxName: 'loa_max',   format: v => v + ' m'  },
  { minName: 'year_min',  maxName: 'year_max',  format: v => v         },
]

const RANGE_NAMES = RANGES.flatMap(r => [r.minName, r.maxName])

const CLOSE_SVG = '<svg class="occasions-filters-recap__tag-icon" width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M10.3535 0.353577L0.353515 10.3536" stroke="#383836"/><path d="M0.353516 0.353577L10.3535 10.3536" stroke="#383836"/></svg>'

// ─── State ─────────────────────────────────────────────────────────────────

const state = {
  model: [], location: [], category: [], warrantyPeriod: [], fuelType: [],
  price_min: '', price_max: '',
  loa_min: '', loa_max: '',
  year_min: '', year_max: '',
}

// ─── DOM helpers ───────────────────────────────────────────────────────────

const syncCheckboxes = (htmlName, value, checked) => {
  document.querySelectorAll(`input[name="${htmlName}"][value="${value}"]`).forEach(el => {
    el.checked = checked
  })
}

const syncTextInputs = (htmlName, value) => {
  document.querySelectorAll(`input[name="${htmlName}"][type="text"]`).forEach(el => {
    el.value = value
  })
}

const getCheckboxLabel = (htmlName, value) => {
  const input = document.querySelector(`input[name="${htmlName}"][value="${value}"]`)
  if (!input) return value
  const label = document.querySelector(`label[for="${input.id}"]`)
  if (!label) return value
  const span = label.querySelector(
    '.occasions-filters__checkbox-text, .occasions-filters__toggle-text'
  )
  return span ? span.textContent.trim() : value
}

// ─── Tag rendering ─────────────────────────────────────────────────────────

const makeTagBtn = (label, onRemove) => {
  const btn = document.createElement('button')
  btn.className = 'occasions-filters-recap__tag'
  btn.type = 'button'
  btn.innerHTML = `<span class="occasions-filters-recap__tag-label">${label}</span>${CLOSE_SVG}`
  btn.addEventListener('click', () => { onRemove(); renderTags(); dispatchFilterChange() })
  return btn
}

const renderTags = () => {
  const container = document.getElementById('occasions-filters-recap-tags')
  if (!container) return
  const clearBtn = document.getElementById('occasions-filters-recap-clear')

  container.querySelectorAll('.occasions-filters-recap__tag').forEach(t => t.remove())

  let count = 0

  Object.entries(CHECKBOX_GROUPS).forEach(([htmlName, stateKey]) => {
    state[stateKey].forEach(value => {
      container.insertBefore(
        makeTagBtn(getCheckboxLabel(htmlName, value), () => {
          state[stateKey] = state[stateKey].filter(v => v !== value)
          syncCheckboxes(htmlName, value, false)
        }),
        clearBtn
      )
      count++
    })
  })

  RANGES.forEach(({ minName, maxName, format }) => {
    const minVal = state[minName]
    const maxVal = state[maxName]
    if (!minVal && !maxVal) return
    // Fall back to current slider value for the untouched side
    const displayMin = minVal || document.querySelector(`input[name="${minName}_range"]`)?.value || ''
    const displayMax = maxVal || document.querySelector(`input[name="${maxName}_range"]`)?.value || ''
    const parts = [displayMin && format(displayMin), displayMax && format(displayMax)].filter(Boolean)
    container.insertBefore(
      makeTagBtn(parts.join(' – '), () => {
        state[minName] = ''
        state[maxName] = ''
        syncTextInputs(minName, '')
        syncTextInputs(maxName, '')
        resetRangeGroup(minName, maxName)
      }),
      clearBtn
    )
    count++
  })

  if (clearBtn) clearBtn.hidden = count === 0
}

// ─── Filter change event ────────────────────────────────────────────────────

const dispatchFilterChange = () => {
  document.dispatchEvent(
    new CustomEvent('occasions:filter-change', { detail: { ...state } })
  )
}

// ─── Form change handler ────────────────────────────────────────────────────

const handleFormChange = (e) => {
  const { name, type, value, checked } = e.target
  if (name === 'sort' || name.endsWith('_range')) return

  if (type === 'checkbox') {
    const stateKey = CHECKBOX_GROUPS[name]
    if (!stateKey) return
    if (checked) {
      if (!state[stateKey].includes(value)) state[stateKey].push(value)
    } else {
      state[stateKey] = state[stateKey].filter(v => v !== value)
    }
    syncCheckboxes(name, value, checked)
  } else if (type === 'text' && RANGE_NAMES.includes(name)) {
    state[name] = value.trim()
    syncTextInputs(name, value.trim())
  }

  renderTags()
  dispatchFilterChange()
}

// ─── Clear all ─────────────────────────────────────────────────────────────

const clearAll = () => {
  Object.entries(CHECKBOX_GROUPS).forEach(([htmlName, stateKey]) => {
    state[stateKey].slice().forEach(v => syncCheckboxes(htmlName, v, false))
    state[stateKey] = []
  })
  RANGE_NAMES.forEach(name => {
    if (state[name]) {
      syncTextInputs(name, '')
      state[name] = ''
    }
  })
  RANGES.forEach(({ minName, maxName }) => resetRangeGroup(minName, maxName))
  renderTags()
  dispatchFilterChange()
}

// ─── Range slider ↔ text input sync ────────────────────────────────────────

const syncRangeSliders = (rangeName, value) => {
  document.querySelectorAll(`input[name="${rangeName}"]`).forEach(el => {
    el.value = value
  })
}

const resetRangeGroup = (minName, maxName) => {
  document.querySelectorAll(`input[name="${minName}_range"]`).forEach(el => { el.value = el.min })
  document.querySelectorAll(`input[name="${maxName}_range"]`).forEach(el => { el.value = el.max })
  updateAllFills(minName + '_range', maxName + '_range')
}

const updateAllFills = (fromRangeName, toRangeName) => {
  document.querySelectorAll(`input[name="${fromRangeName}"]`).forEach(fromEl => {
    const wrapper = fromEl.closest('.occasions-filters__range-slider')
    if (!wrapper) return
    const toEl = wrapper.querySelector(`input[name="${toRangeName}"]`)
    if (!toEl) return
    const absMin = +fromEl.min
    const absMax = +fromEl.max
    const fromPct = ((+fromEl.value - absMin) / (absMax - absMin)) * 100
    const toPct   = ((+toEl.value   - absMin) / (absMax - absMin)) * 100
    wrapper.style.setProperty('--fp-from', fromPct + '%')
    wrapper.style.setProperty('--fp-to',   toPct   + '%')
  })
}

const initSliders = () => {
  const processed = new Set()

  document.querySelectorAll('.occasions-filters__range-slider').forEach(sliderEl => {
    const fromEl = sliderEl.querySelector('.occasions-filters__range-slider-input--from')
    const toEl   = sliderEl.querySelector('.occasions-filters__range-slider-input--to')
    if (!fromEl || !toEl || processed.has(fromEl.name)) return
    processed.add(fromEl.name)

    const minName = fromEl.name.replace('_range', '')
    const maxName = toEl.name.replace('_range', '')

    // Listen on ALL sliders with these names (sidebar + modal)
    document.querySelectorAll(`input[name="${fromEl.name}"]`).forEach(el => {
      el.addEventListener('input', () => {
        const currentTo = state[maxName] !== '' ? parseNum(state[maxName]) : +el.max
        const newVal = Math.min(+el.value, currentTo)
        el.value = newVal
        state[minName] = String(newVal)
        syncTextInputs(minName, String(newVal))
        syncRangeSliders(fromEl.name, String(newVal))
        updateAllFills(fromEl.name, toEl.name)
        renderTags()
      })
      el.addEventListener('change', dispatchFilterChange)
    })

    document.querySelectorAll(`input[name="${toEl.name}"]`).forEach(el => {
      el.addEventListener('input', () => {
        const currentFrom = state[minName] !== '' ? parseNum(state[minName]) : +el.min
        const newVal = Math.max(+el.value, currentFrom)
        el.value = newVal
        state[maxName] = String(newVal)
        syncTextInputs(maxName, String(newVal))
        syncRangeSliders(toEl.name, String(newVal))
        updateAllFills(fromEl.name, toEl.name)
        renderTags()
      })
      el.addEventListener('change', dispatchFilterChange)
    })
  })
}

// ─── Init ───────────────────────────────────────────────────────────────────

const initFiltersRecap = () => {
  if (!document.querySelector('.occasions-filters-recap')) return

  // Read initial state from sidebar form (first .occasions-filters in DOM)
  const form = document.querySelector('.occasions-filters')
  if (form) {
    Object.keys(CHECKBOX_GROUPS).forEach(htmlName => {
      const stateKey = CHECKBOX_GROUPS[htmlName]
      const seen = new Set()
      form.querySelectorAll(`input[name="${htmlName}"]:checked`).forEach(input => {
        if (!seen.has(input.value)) {
          seen.add(input.value)
          state[stateKey].push(input.value)
        }
      })
    })
    RANGE_NAMES.forEach(name => {
      const input = form.querySelector(`input[name="${name}"]`)
      if (input) state[name] = input.value.trim()
    })
  }

  renderTags()
  initSliders()

  document.querySelectorAll('.occasions-filters').forEach(f => {
    f.addEventListener('change', handleFormChange)
  })

  document.querySelectorAll('#occasions-filters-recap-clear, .occasions-filters-modal__footer-clear').forEach(btn => {
    btn.addEventListener('click', clearAll)
  })

  // Délégation pour le bouton dans le grid (remplacé dynamiquement par AJAX)
  document.addEventListener('click', e => {
    if (e.target.closest('.occasions-list__empty-state-cta')) clearAll()
  })
}

if (document.readyState !== 'loading') {
  initFiltersRecap()
} else {
  document.addEventListener('DOMContentLoaded', initFiltersRecap)
}
